export type DemoAccount = {
  phone: string;
  salt: string;
  passwordHash: string;
  createdAt: string;
};

export type DemoSession = {
  phone: string;
  signedInAt: string;
};

export type AuthValidation = {
  phoneError?: string;
  passwordError?: string;
};

const ACCOUNT_KEY = "bitevent-guide:demo-account";
const SESSION_KEY = "bitevent-guide:demo-session";

export function normalizePhone(phone: string) {
  return phone.trim().replace(/[\s()-]/g, "");
}

export function validateDemoCredentials(
  phoneInput: string,
  password: string,
): AuthValidation {
  const phone = normalizePhone(phoneInput);
  return {
    phoneError: /^\+?\d{8,15}$/.test(phone)
      ? undefined
      : "请输入 8–15 位手机号，可包含国家区号",
    passwordError:
      password.length < 6
        ? "密码至少需要 6 位"
        : password.length > 64
          ? "密码不能超过 64 位"
          : undefined,
  };
}

function toHex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function hashPassword(password: string, salt: string) {
  const bytes = new TextEncoder().encode(`${salt}:${password}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return toHex(new Uint8Array(digest));
}

function createSalt() {
  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  return toHex(salt);
}

export function getDemoAccount(): DemoAccount | null {
  try {
    const stored = localStorage.getItem(ACCOUNT_KEY);
    return stored ? (JSON.parse(stored) as DemoAccount) : null;
  } catch {
    return null;
  }
}

export function getDemoSession(): DemoSession | null {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? (JSON.parse(stored) as DemoSession) : null;
  } catch {
    return null;
  }
}

export async function registerDemoAccount(phoneInput: string, password: string) {
  const phone = normalizePhone(phoneInput);
  const salt = createSalt();
  const account: DemoAccount = {
    phone,
    salt,
    passwordHash: await hashPassword(password, salt),
    createdAt: new Date().toISOString(),
  };
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  const session = { phone, signedInAt: new Date().toISOString() };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export async function loginDemoAccount(phoneInput: string, password: string) {
  const phone = normalizePhone(phoneInput);
  const account = getDemoAccount();
  if (!account || account.phone !== phone) return null;
  const passwordHash = await hashPassword(password, account.salt);
  if (passwordHash !== account.passwordHash) return null;
  const session = { phone, signedInAt: new Date().toISOString() };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function logoutDemoAccount() {
  sessionStorage.removeItem(SESSION_KEY);
}
